import { importLibrary, setOptions } from "@googlemaps/js-api-loader";
import { useCallback, useMemo, useState } from "react";

import { reportError } from "utilities/errorHelpers";

import { MapApiContext, MapApiStatus } from "./MapApiContext";

interface IMapApiProviderProps {
    enabled: boolean;
    apiKey: string;
    children: React.ReactNode;
}

const MapApiProvider: React.FC<IMapApiProviderProps> = ({ enabled, apiKey, children }) => {
    const [status, setStatus] = useState<MapApiStatus>(MapApiStatus.NotLoaded);

    const loadMapApi = useCallback(async () => {
        if (status === MapApiStatus.NotLoaded) {
            if (!enabled) {
                setStatus(MapApiStatus.Unavailable);
                return MapApiStatus.Unavailable;
            }
            try {
                setOptions({ key: apiKey });

                const status = await Promise.all([
                    importLibrary("maps"),
                    importLibrary("places"),
                ]).then(async () => {
                    setStatus(MapApiStatus.Loaded);
                    return MapApiStatus.Loaded;
                });

                return status;
            } catch (error) {
                reportError(error);
                setStatus(MapApiStatus.Error);
                return MapApiStatus.Error;
            }
        } else {
            return status;
        }
    }, [status, enabled, apiKey]);

    const getStatus = useCallback(async () => {
        const currentStatus = await loadMapApi();
        return currentStatus;
    }, [loadMapApi]);

    const contextValue = useMemo(
        () => ({
            status: getStatus,
        }),
        [getStatus],
    );

    return <MapApiContext.Provider value={contextValue}>{children}</MapApiContext.Provider>;
};

export default MapApiProvider;
